import { Injectable } from '@angular/core';
import { confirmNegative, ModalMessageInfo } from '@app/core/components/modal';
import { UserService } from '@app/core/services/user.service';
import { SearchDossiers, SearchDossiersFail, setSearchResults } from '@app/pcs/actions/dossier-search';
import { SelectFilter } from '@app/pcs/actions/filter';
import * as GridLabelActions from '@app/pcs/actions/grid-label';
import * as LabelActions from '@app/pcs/actions/label';
import { LabelActionTypes, SearchDossiersByLabels } from '@app/pcs/actions/label';
import { SelectRecentActivity } from '@app/pcs/actions/recent-activity';
import { InitShareForm } from '@app/pcs/actions/share-filter-label';
import { DossierSearchType, UserLabel } from '@app/pcs/models';
import * as fromPcs from '@app/pcs/reducer';
import { SharedObjectType } from '@app/pcs/reducer/share-filter-label';
import { UserLabelDataService } from '@app/pcs/services/user-label-data.service';
import { getCreatorFullName, getLabelId } from '@app/pcs/util/user-label';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { hasValue, isFalse, isNotEmpty, isTrue, mapPayload } from '@util';
import * as R from 'ramda';
import { iif, of } from 'rxjs';
import { catchError, exhaustMap, switchMap, withLatestFrom } from 'rxjs/operators';

const removeLabelMessage = (message: string, messageParams: string[]): ModalMessageInfo => ({
  titleText: 'dashboard.delete.label',
  buttonText: 'general.action.delete',
  message,
  messageParams
});

@Injectable()
export class LabelEffects {

  private selectedLabelIds$ = this.store.pipe(select(fromPcs.getSelectedLabelIds));

  fetchLabels$ = createEffect(() => this.actions$.pipe(
    ofType<LabelActions.FetchLabels>(LabelActionTypes.FetchLabels),
    withLatestFrom(this.store.pipe(select(fromPcs.getLabels))),
    exhaustMap(([, cachedLabels]) =>
      hasValue(cachedLabels)
        ? of(new LabelActions.FetchLabelsSuccess(cachedLabels))
        : this.userLabelDataService.fetchLabels().pipe(
          switchMap((labels: UserLabel[]) => of(new LabelActions.FetchLabelsSuccess(labels))),
          catchError(error => of(new LabelActions.FetchLabelsFail(error)))
        )
    )
  ));

  selectLabel$ = createEffect(() => this.actions$.pipe(
    ofType<LabelActions.SelectLabel>(LabelActionTypes.SelectLabel),
    withLatestFrom(this.selectedLabelIds$),
    switchMap(([_, selectedLabelIds]: [any, number[]]) => {
      const defaultFilterId = this.userService.getDefaultFilterId();
      return iif(() => isNotEmpty(selectedLabelIds),
        iif(() => R.length(selectedLabelIds) !== 1,
          of(new SearchDossiersByLabels()),
          [new SearchDossiersByLabels(), new InitShareForm({sharedObjectType: SharedObjectType.LABEL,
            sharedObjectId: R.head(selectedLabelIds)})]),
        iif(() => hasValue(defaultFilterId), of(new SelectFilter(defaultFilterId)), of(new SelectRecentActivity()))
      );
    })
  ));

  searchDossiersByLabels$ = createEffect(() => this.actions$.pipe(
    ofType(LabelActionTypes.SearchDossiersByLabels),
    withLatestFrom(this.selectedLabelIds$),
    exhaustMap(([, labelIds]) =>
      this.userLabelDataService.search(labelIds).pipe(
        switchMap(({
          searchResults,
          userGridConfiguration
        }: any) => [setSearchResults(searchResults, DossierSearchType.LABELS, userGridConfiguration)]),
        catchError(error => of(new SearchDossiersFail(error)))
      )
    )
  ));

  confirmLabelRemoval$ = createEffect(() => this.actions$.pipe(
    ofType<LabelActions.ConfirmLabelRemoval>(LabelActionTypes.ConfirmLabelRemoval),
    mapPayload(),
    exhaustMap((userLabel: UserLabel) =>
      this.userLabelDataService.isLabelUsedInDossiers(getLabelId(userLabel)).pipe(
        switchMap((isLabelUsedInDossiers: boolean) => {
          const {label, creator} = userLabel;
          const message = this.removeLabelConfirmationMessage(isLabelUsedInDossiers, creator);
          const messageParams = isTrue(creator)
            ? [label.name]
            : [getCreatorFullName(userLabel), label.name];
          return of(confirmNegative(removeLabelMessage(message, messageParams),
            [new LabelActions.RemoveLabel(label.id)]));
        }
        ))
    )));

  removeLabel$ = createEffect(() => this.actions$.pipe(
    ofType<LabelActions.RemoveLabel>(LabelActionTypes.RemoveLabel),
    mapPayload(),
    exhaustMap((labelId) =>
      this.userLabelDataService.removeLabel(labelId).pipe(
        switchMap(() => [
          new LabelActions.RemoveLabelSuccess(labelId),
          new SearchDossiers()
        ]),
        catchError((error: Error) => of(new LabelActions.RemoveLabelFail(error)))
      )
    )
  ));

  updateLabel$ = createEffect(() => this.actions$.pipe(
    ofType<LabelActions.SaveLabel>(LabelActionTypes.SaveLabel),
    mapPayload(),
    exhaustMap((label: UserLabel) =>
      this.userLabelDataService.saveLabel(label).pipe(
        switchMap((updatedLabel: UserLabel) => [
          new LabelActions.SaveLabelSuccess(updatedLabel),
          new GridLabelActions.UpdateLabel(updatedLabel)
        ]),
        catchError((error: Error) => of(new LabelActions.SaveLabelFail(error)))
      )
    )
  ));

  removeLabelConfirmationMessage = R.cond([
    [(usedInDossier, labelCreator) => isTrue(usedInDossier) && isTrue(labelCreator), R.always('label.removeLabel')],
    [(usedInDossier, labelCreator) => isFalse(usedInDossier) && isTrue(labelCreator), R.always('dashboard.delete.label.infoMessage')],
    [(usedInDossier, labelCreator) => isFalse(labelCreator),R.always('action.delete.shared.label')],
  ]);

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private userLabelDataService: UserLabelDataService,
    private userService: UserService
  ) {
  }
}
